/**
 * Scroll Lock Manager
 * 
 * A reference-counted, iOS-safe scroll lock utility.
 * Supports nested modals by incrementing/decrementing lock count.
 * Only removes scroll lock when all modals are closed.
 * 
 * IMPORTANT: Does NOT use position:fixed body lock on iOS to avoid
 * breaking file inputs, form submissions, and other native behaviors.
 * 
 * CRITICAL: On mobile, scroll lock is more conservative to prevent
 * "Body is disturbed or locked" type errors during form operations.
 */

let lockCount = 0;
let savedStyles = {};

// Detect iOS for special handling
const isIOS = () => {
  if (typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// Detect if on mobile device
const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    window.innerWidth < 768;
};

// Get scrollbar width to prevent layout shift
const getScrollbarWidth = () => {
  if (typeof document === 'undefined') return 0;
  const scrollDiv = document.createElement('div');
  scrollDiv.style.cssText = 'width:100px;height:100px;overflow:scroll;position:absolute;top:-9999px';
  document.body.appendChild(scrollDiv);
  const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
  document.body.removeChild(scrollDiv);
  return scrollbarWidth;
};

/**
 * Lock body scroll
 * Safe for nested modals - only applies lock on first call
 * On mobile/iOS, uses minimal locking to avoid form submission issues
 */
export const lock = () => {
  lockCount++;
  
  // Only apply lock on first call
  if (lockCount === 1) {
    if (typeof document === 'undefined') return;
    
    const body = document.body;
    const html = document.documentElement;
    const scrollbarWidth = getScrollbarWidth();
    
    // Save current styles
    savedStyles = {
      bodyOverflow: body.style.overflow,
      bodyPaddingRight: body.style.paddingRight,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      htmlOverflow: html.style.overflow,
      scrollY: window.scrollY
    };
    
    // Apply scroll lock - more conservative on mobile to avoid form issues
    if (isIOS() || isMobile()) {
      // Mobile/iOS: Use very minimal approach to avoid breaking forms
      // Only set overflow hidden, no touch-action changes
      body.style.overflow = 'hidden';
      html.style.overflow = 'hidden';
      // DO NOT set touch-action on mobile - this can cause form submission issues
    } else {
      // Desktop: Standard overflow hidden + scrollbar compensation
      body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        body.style.paddingRight = `${scrollbarWidth}px`;
      }
    }
    
    // Add class for CSS hooks
    body.classList.add('dmv-scroll-locked');
    
    console.log('[ScrollLock] Locked. Count:', lockCount, 'Mobile:', isMobile());
  }
};

/**
 * Unlock body scroll
 * Safe for nested modals - only removes lock when all modals closed
 */
export const unlock = () => {
  lockCount = Math.max(0, lockCount - 1);
  
  // Only remove lock when all modals are closed
  if (lockCount === 0) {
    if (typeof document === 'undefined') return;
    
    const body = document.body;
    const html = document.documentElement;
    
    // Restore saved styles
    body.style.overflow = savedStyles.bodyOverflow || '';
    body.style.paddingRight = savedStyles.bodyPaddingRight || '';
    body.style.position = savedStyles.bodyPosition || '';
    body.style.top = savedStyles.bodyTop || '';
    body.style.width = savedStyles.bodyWidth || '';
    body.style.touchAction = '';
    html.style.overflow = savedStyles.htmlOverflow || '';
    
    // Remove class
    body.classList.remove('dmv-scroll-locked');
    
    // Reset saved styles
    savedStyles = {};
    
    console.log('[ScrollLock] Unlocked. Count:', lockCount);
  } else {
    console.log('[ScrollLock] Decremented. Count:', lockCount);
  }
};

/**
 * Force unlock all locks
 * Use sparingly - only for error recovery
 */
export const forceUnlock = () => {
  lockCount = 0;
  unlock();
  console.warn('[ScrollLock] Force unlocked');
};

/**
 * Get current lock count
 * Useful for debugging
 */
export const getLockCount = () => lockCount;

/**
 * Check if body is currently locked
 */
export const isLocked = () => lockCount > 0;

/**
 * React hook for scroll locking
 * Automatically unlocks on unmount
 */
export const useScrollLock = (shouldLock = true) => {
  // This is a simple implementation - for React, import and use in useEffect
  // See usage example below
};

export default {
  lock,
  unlock,
  forceUnlock,
  getLockCount,
  isLocked
};

/*
 * USAGE EXAMPLE:
 * 
 * import { useEffect } from 'react';
 * import { lock, unlock } from '../utils/scrollLock';
 * 
 * function MyModal({ isOpen, onClose }) {
 *   useEffect(() => {
 *     if (isOpen) {
 *       lock();
 *     }
 *     return () => {
 *       if (isOpen) {
 *         unlock();
 *       }
 *     };
 *   }, [isOpen]);
 *   
 *   if (!isOpen) return null;
 *   
 *   return <div>Modal content</div>;
 * }
 */
